import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { BASE_URL } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import '../styles/events.css';

function Login() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [registerData, setRegisterData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    event_ids: [],
  });
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (activeTab !== 'register') return;
    setEventsLoading(true);
    fetch(`${BASE_URL}/events?status=published&upcoming=true`, {
      headers: { [TOKEN_HEADER]: generateToken() },
    })
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.events || data.data || []);
        setEvents(list);
      })
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  }, [activeTab]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({ ...prev, [name]: value }));
  };

  const handleEventSelect = (e) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => Number(opt.value));
    setRegisterData(prev => ({ ...prev, event_ids: selected }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${BASE_URL}/workshops/attendees/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Keep both token keys for compatibility with existing consumers.
        localStorage.setItem('token', data.token);
        localStorage.setItem('attendeeToken', data.token);
        localStorage.setItem('userId', String(data.attendee?.id || ''));
        localStorage.setItem('userEmail', data.attendee?.email || formData.email);
        localStorage.setItem(
          'userName',
          [data.attendee?.first_name, data.attendee?.last_name].filter(Boolean).join(' ') || ''
        );

        navigate('/lms');
      } else {
        setError(data.error || data.message || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      const payload = {
        first_name: registerData.first_name,
        last_name: registerData.last_name,
        email: registerData.email,
        password: registerData.password,
        ...(registerData.phone && { phone: registerData.phone }),
        ...(registerData.event_ids.length > 0 && { event_ids: registerData.event_ids }),
      };

      const response = await fetch(`${BASE_URL}/workshops/attendees/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMsg('Attendee registered successfully! You can now sign in.');
        setActiveTab('login');
        setRegisterData({ first_name: '', last_name: '', email: '', password: '', phone: '', event_ids: [] });
      } else if (response.status === 409) {
        setError('An account with this email already exists.');
      } else {
        setError(data.message || data.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Register error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ep-page ep-login-page">
      <SEO 
        title="Login | Lead with Tech LMS"
        description="Login to access your Lead with Tech Learning Management System account"
        keywords="login, lms, learning management system, lead with tech"
      />
      <div className="ep-hero-glow" aria-hidden="true" />

      <div className="ep-login-wrap">
        <Link to="/" className="ep-login-back">← Back to Home</Link>

        <div className="ep-login-card">
          <div className="ep-login-header">
            <span className="ep-live-badge">
              <span className="ep-pulse-dot" />
              LMS Portal
            </span>
            <h1 className="ep-login-title">Lead with Tech Access</h1>
            <p className="ep-muted ep-login-sub">
              {activeTab === 'login'
                ? 'Sign in to continue your learning journey.'
                : 'Register a workshop attendee and assign upcoming events.'}
            </p>
          </div>

          <div className="ep-login-tabs">
            <button
              type="button"
              className={`ep-login-tab${activeTab === 'login' ? ' ep-login-tab-active' : ''}`}
              onClick={() => { setActiveTab('login'); setError(''); setSuccessMsg(''); }}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`ep-login-tab${activeTab === 'register' ? ' ep-login-tab-active' : ''}`}
              onClick={() => { setActiveTab('register'); setError(''); setSuccessMsg(''); }}
            >
              Register Attendee
            </button>
          </div>

          {successMsg && (
            <div className="ep-login-success">{successMsg}</div>
          )}

          {error && (
            <div className="ep-login-error">{error}</div>
          )}

          {activeTab === 'login' && (
            <form className="ep-rsvp-form ep-login-form" onSubmit={handleSubmit}>
              <div className="ep-form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@email.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="ep-form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button type="submit" className="ep-submit-btn" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In →'}
              </button>

              <p className="ep-login-hint ep-muted">
                Forgot your password?{' '}
                <button type="button" className="ep-link-btn" onClick={() => alert('Password reset — coming soon.')}>Reset it</button>
              </p>
            </form>
          )}

          {activeTab === 'register' && (
            <form className="ep-rsvp-form ep-login-form" onSubmit={handleRegisterSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="ep-form-group">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={registerData.first_name}
                    onChange={handleRegisterChange}
                    placeholder="Riya"
                    required
                  />
                </div>
                <div className="ep-form-group">
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={registerData.last_name}
                    onChange={handleRegisterChange}
                    placeholder="Sharma"
                    required
                  />
                </div>
              </div>

              <div className="ep-form-group">
                <label htmlFor="reg-email">Email Address</label>
                <input
                  type="email"
                  id="reg-email"
                  name="email"
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  placeholder="riya.sharma@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="ep-form-group">
                <label htmlFor="reg-password">Password</label>
                <input
                  type="password"
                  id="reg-password"
                  name="password"
                  value={registerData.password}
                  onChange={handleRegisterChange}
                  placeholder="StrongPass@123"
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="ep-form-group">
                <label htmlFor="reg-phone">Phone</label>
                <input
                  type="tel"
                  id="reg-phone"
                  name="phone"
                  value={registerData.phone}
                  onChange={handleRegisterChange}
                  placeholder="919876543210"
                />
              </div>

              <div className="ep-form-group">
                <label htmlFor="event_ids">Upcoming Events (admin selection)</label>
                {eventsLoading ? (
                  <p className="ep-muted" style={{ fontSize: '13px', marginTop: '6px' }}>Loading upcoming events…</p>
                ) : events.length === 0 ? (
                  <p className="ep-muted" style={{ fontSize: '13px', marginTop: '6px' }}>No upcoming events available right now.</p>
                ) : (
                  <select
                    id="event_ids"
                    multiple
                    value={registerData.event_ids.map(String)}
                    onChange={handleEventSelect}
                    style={{ minHeight: '120px' }}
                  >
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.title || event.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <button type="submit" className="ep-submit-btn" disabled={loading}>
                {loading ? 'Registering…' : 'Register Attendee →'}
              </button>
            </form>
          )}

          <div className="ep-login-perks">
            <p className="ep-login-perks-title">What you get</p>
            <div className="ep-login-perk-list">
              {[
                { icon: '📅', title: 'Assigned Workshops', desc: 'Users only see workshops linked to their profile' },
                { icon: '📎', title: 'Event Resources', desc: 'Access slides, links and workshop notes' },
                { icon: '📊', title: 'Progress Tracking', desc: 'Monitor attendance and learning progress' },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="ep-login-perk">
                  <span className="ep-login-perk-icon">{icon}</span>
                  <div>
                    <div className="ep-login-perk-title">{title}</div>
                    <div className="ep-muted ep-login-perk-desc">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
